import { useEffect, useRef, useState } from 'react';

/*
 * The parts of a story that are not in the page's first load: its hand (the
 * pointer and the cues), a drawer's editors, another page of the app. A
 * story opens on what the page already has; the rest is asked for once the
 * page has finished loading and settled (a second after the window's `load`
 * event, then when the browser is idle where it can say so), so none of it
 * competes with the first paint. The story's playback holds before any beat
 * that needs a part until that part has arrived (see useStory). Loaded
 * modules are kept for the page's lifetime (by loader), so a replay (a fresh
 * scene) or a remount has them at once.
 */

/** How long after the window's `load` event parts are asked for. */
export const SETTLE_MS = 1000;

const loaded = new Map();
const loading = new Map();
const retried = new Set();
let settled = null;

/**
 * Resolves a second after the window's `load` event (at once from then on).
 * Asked once the page has loaded, it counts the second from then (useParts
 * asks as soon as a story mounts, with the page).
 */
export function afterPageLoad() {
    if (!settled) {
        settled = new Promise((resolve) => {
            const wait = () => setTimeout(resolve, SETTLE_MS);
            if (document.readyState === 'complete') wait();
            else window.addEventListener('load', wait, { once: true });
        });
    }

    return settled;
}

/** Calls `callback` once the browser is idle, where it can say so; at once where it cannot. */
function whenIdle(callback) {
    if (window.requestIdleCallback) window.requestIdleCallback(callback, { timeout: 1500 });
    else callback();
}

/**
 * Starts loading a part with `loader` (once), and resolves with its module.
 * A chunk that fails to load is said so (in development) and asked for once
 * more on the next request; failing again, it stays failed and the story
 * holds where it is.
 */
export function loadPart(loader) {
    if (!loading.has(loader)) {
        loading.set(loader, loader().then((module) => {
            loaded.set(loader, module);
            return module;
        }, (error) => {
            if (import.meta.env.DEV) console.error('[vignette] a story part failed to load', error);
            if (!retried.has(loader)) {
                retried.add(loader);
                loading.delete(loader);
            }
            throw error;
        }));
    }

    return loading.get(loader);
}

/** The module `loader` loads, if it has arrived; else null. */
export function loadedPart(loader) {
    return loaded.get(loader) ?? null;
}

/**
 * The parts a story (or a page) asks for: `requests` is a list of
 * `{ name, loader, enabled, now }` (the same names on every render; `enabled`
 * may be a function of the modules so far, for a part that waits on
 * another). Returns `{ [name]: module | null }`: each asked for once it is
 * enabled and the page has settled (see afterPageLoad, then idle), or at
 * once with `now` (a part already on screen); null until it arrives (at once
 * for a part already loaded, as on a replay). Nothing is requested while
 * `enabled` is false. A part that fails to load is asked for once more.
 */
export function useParts(requests) {
    const [modules, setModules] = useState(() => Object.fromEntries(
        requests.map(({ name, loader }) => [name, loader ? loadedPart(loader) : null]),
    ));
    const wanted = requests.filter(({ name, loader, enabled }) => (
        loader && !modules[name] && (typeof enabled === 'function' ? enabled(modules) : enabled)
    ));
    // One more request for a part that failed (see loadPart).
    const [attempt, setAttempt] = useState(0);
    const failed = useRef(new Set());
    const key = `${wanted.map(({ name, now }) => `${name}${now ? '!' : ''}`).join('|')}#${attempt}`;

    // The page's settling is counted from its load, not from the first part asked for.
    useEffect(() => {
        afterPageLoad();
    }, []);

    useEffect(() => {
        if (wanted.length === 0) return undefined;
        let cancelled = false;
        const load = ({ name, loader }) => loadPart(loader).then((module) => {
            if (!cancelled) setModules((current) => ({ ...current, [name]: module }));
        }, () => {
            if (cancelled || failed.current.has(name)) return;
            failed.current.add(name);
            setAttempt((count) => count + 1);
        });
        wanted.filter(({ now }) => now).forEach(load);
        const gated = wanted.filter(({ now }) => !now);
        if (gated.length > 0) {
            afterPageLoad().then(() => whenIdle(() => {
                if (!cancelled) gated.forEach(load);
            }));
        }

        return () => {
            cancelled = true;
        };
        // `key` names exactly the parts this effect asks for.
    }, [key]);

    return modules;
}

/**
 * A story's part loaders, reached through a module of loaders that is itself
 * loaded lazily (`loadModule`, e.g. `() => import('./heroParts')`), so the
 * lists of chunks each part needs stay out of the page's first load:
 * `{ [name]: loader }`, stable functions (parts are remembered by loader).
 */
export function partLoaders(loadModule, names) {
    return Object.freeze(Object.fromEntries(names.map((name) => [name, () => loadModule().then((loaders) => loaders[name]())])));
}

/** Forgets every loaded part and the settled page (tests only). */
export function resetParts() {
    loaded.clear();
    loading.clear();
    retried.clear();
    settled = null;
}
